import { Component, computed, input, model, output, signal } from '@angular/core';
import { AgendaBlockComponent } from '../agenda-block/agenda-block.component';
import { ButtonComponent } from '../button/button.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { type CalendarBlock, type CalendarBlockStatus, type CalendarConfig, type CalendarCourtRow, type CalendarViewMode } from './availability-reservation-calendar.model';
import { ALL_COURTS_FILTER, DAY_HOUR_ICON, DEFAULT_CALENDAR_CONFIG, NIGHT_STARTS_AT_HOUR, OCCUPIED_STATUS } from './availability-reservation-calendar.constants';

@Component({
  selector: 'lib-availability-reservation-calendar',
  imports: [AgendaBlockComponent, LoadingSpinnerComponent, ButtonComponent],
  host: { style: 'display: block' },
  templateUrl: './availability-reservation-calendar.component.html',
  styleUrl: './availability-reservation-calendar.component.css',
})
export class AvailabilityReservationCalendarComponent {
  readonly title = input('Calendario');
  readonly subtitle = input('Cruce de horarios por cancha');
  readonly view = model<CalendarViewMode>('day');
  readonly startHour = input(7);
  readonly endHour = input(22);
  readonly courts = input.required<CalendarCourtRow[]>();
  readonly config = input<CalendarConfig>(DEFAULT_CALENDAR_CONFIG);
  readonly loading = input(false);
  /** true → the data request failed; shows a retry state instead of the grid. */
  readonly error = input(false);
  /** Optional message shown in the retry state. */
  readonly errorMessage = input('No se pudo cargar la disponibilidad.');

  readonly blockClicked = output<{ courtIndex: number; blockIndex: number }>();
  /** Emitted when the user clicks "Reintentar" in the error state. */
  readonly retry = output<void>();

  readonly showInternalInfoOnBlock = computed(
    () => this.config().showInternalInfoOnBlock ?? DEFAULT_CALENDAR_CONFIG.showInternalInfoOnBlock
  );

  readonly showTimeOnBlock = computed(
    () => this.config().showTimeOnBlock ?? DEFAULT_CALENDAR_CONFIG.showTimeOnBlock
  );

  readonly showDetailedStatus = computed(
    () => this.config().showDetailedStatus ?? DEFAULT_CALENDAR_CONFIG.showDetailedStatus
  );

  readonly showHeaderHours = computed(
    () => this.config().showHeaderHours ?? DEFAULT_CALENDAR_CONFIG.showHeaderHours
  );

  readonly selectedCourt = signal<string>(ALL_COURTS_FILTER);

  readonly filteredCourts = computed(() => {
    const filter = this.selectedCourt();
    if (filter === ALL_COURTS_FILTER) {
      return this.courts();
    }
    return this.courts().filter((c) => c.name === filter);
  });

  readonly totalHours = computed(() => this.endHour() - this.startHour());

  readonly timeHeaders = computed(() => {
    const headers: { hour: number; label: string }[] = [];
    for (let h = this.startHour(); h < this.endHour(); h++) {
      headers.push({ hour: h, label: `${String(h).padStart(2, '0')}:00` });
    }
    return headers;
  });

  readonly gridStyle = computed(() => ({
    'grid-template-columns': `max-content repeat(${this.totalHours()}, 90px)`,
  }));

  readonly trackStyle = computed(() => ({
    'grid-column': `span ${this.totalHours()}`,
  }));

  onCourtFilterChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedCourt.set(value);
  }

  /** Sun icon shown for daytime hours (night hours render a white moon SVG instead, see template) */
  readonly dayHourIcon = DAY_HOUR_ICON;

  /** True from `NIGHT_STARTS_AT_HOUR` onward — darkens the header cell and swaps to the moon icon */
  isNightHour(hour: number): boolean {
    return hour >= NIGHT_STARTS_AT_HOUR;
  }

  /** Real status when detailed, otherwise collapses any non-"disponible" status to a generic occupied color */
  effectiveStatus(block: CalendarBlock): CalendarBlockStatus {
    if (this.showDetailedStatus() || block.status === 'disponible') {
      return block.status;
    }
    return OCCUPIED_STATUS;
  }

  /** Block width: % minus the 4px gutter so adjacent blocks always keep the same gap, in every view */
  blockWidth(block: CalendarBlock): string {
    const width = ((block.end - block.start) / this.totalHours()) * 100;
    return `calc(${width}% - 4px)`;
  }
}
